import { getDiscipline } from "@shared/labs/disciplines/registry";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Eye, Loader2, Pause, Play, Plus, Save, Trash2, Upload } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { rpcClient } from "@/lib/rpc";
import { CorpusAddDrawer } from "./corpus-add-drawer";
import { CorpusProfileDrawer } from "./corpus-profile-drawer";
import { VersionsPanel } from "./versions-panel";

interface CorpusFixture {
  id: string;
  /** Raw fixture identifier — opaque token like `corpus-…`, `from-eval-<uuid>`,
   *  `[bad] from-eval-<uuid>`, or `[pending] from-eval-<uuid>`. Used to
   *  extract the source evaluation ID for the corrections-history badge.
   *  NOT shown to users. */
  candidateName: string;
  displayName: string;
  hired: boolean;
  /** Tri-state decision the CHRO assigned at promote time. `pending` means
   *  the row is parked — it stays in the corpus list (so the operator
   *  remembers the profile) but is excluded from calibration runs. */
  decision?: "hired" | "rejected" | "pending";
  /** Server-overlaid: latest CHRO correction wins over the seed value. */
  expectedScores: Record<string, number>;
  expectedSkills: Record<string, number>;
  /** Pre-correction values for keys whose value shifted; empty when nothing
   *  was adjusted. Used to render an "x.y → z.w" comparison strip. */
  originalScores?: Record<string, number>;
  originalSkills?: Record<string, number>;
  updatedAt: string;
  disabled?: boolean;
}

interface AuditRowSlim {
  id: string;
  evaluationId: string | null;
  status: string;
}

/**
 * Fixtures promoted from the candidate-intelligence flow embed the source
 * evaluation ID in their `candidateName` token (`from-eval-<uuid>` or
 * `[bad] from-eval-<uuid>`). Pure reference profiles (`corpus-…`) return null
 * and never get a corrections badge.
 */
function extractSourceEvaluationId(s: string | null | undefined): string | null {
  if (!s) return null;
  const m = /from-eval-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i.exec(s);
  return m?.[1] ?? null;
}

const DIM_KEYS = ["academic", "trajectory", "tenure", "founder"] as const;
type DimKey = (typeof DIM_KEYS)[number];

const DIM_LABEL: Record<DimKey, string> = {
  academic: "Acadêmico",
  trajectory: "Trajetória",
  tenure: "Estabilidade",
  founder: "Fundador",
};

/** Legacy seeded fixtures store skill keys in short form (`pm`, `sm`, ...).
 *  New fixtures created via promote-to-fixture use the long form
 *  (`project_management`, ...). Only applied for the FDE discipline (or when
 *  no discipline filter is active — since FDE fixtures are the dominant
 *  population and would otherwise misrender). */
const LEGACY_SHORT_TO_LONG_FDE: Record<string, string> = {
  pm: "project_management",
  sm: "stakeholder_management",
  ba: "business_acumen",
  hf: "hr_fluency",
  ps: "product_sense",
};

function normalizeSkillScores(
  s: Record<string, number>,
  disciplineSlug: string | undefined,
): Record<string, number> {
  if (disciplineSlug !== undefined && disciplineSlug !== "forward-deployed") return s;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(s)) {
    out[LEGACY_SHORT_TO_LONG_FDE[k] ?? k] = v;
  }
  return out;
}

function skillLabelFor(skillId: string, disciplineSlug: string | undefined): string {
  // Resolve via the registry; fall back to forward-deployed for legacy
  // single-discipline views (today's default behavior).
  const slug = disciplineSlug ?? "forward-deployed";
  const spec = getDiscipline(slug);
  return spec?.displayLabels?.[skillId] ?? skillId;
}

/**
 * Returns the skill key set for the corpus grid.
 *
 * When `disciplineSlug` is provided and resolves in the registry, the
 * discipline's canonical `skillIds` are used (in spec order). Otherwise the
 * grid shows the union of keys actually observed in loaded fixtures — so
 * legacy mixed-discipline corpora remain viewable without a filter.
 */
function useSkillKeysForDiscipline(
  disciplineSlug: string | undefined,
  fixtures: { expectedSkills: Record<string, number> }[],
): readonly string[] {
  return useMemo(() => {
    if (disciplineSlug) {
      const spec = getDiscipline(disciplineSlug);
      if (spec) return spec.skillIds;
    }
    const observed = new Set<string>();
    for (const f of fixtures) {
      for (const k of Object.keys(normalizeSkillScores(f.expectedSkills, disciplineSlug))) {
        observed.add(k);
      }
    }
    return Array.from(observed).sort();
  }, [disciplineSlug, fixtures]);
}

type CorpusView = "evidences" | "skills";

/** Color-coded grade cell. Click to edit inline. Color reflects quality —
 *  green/amber/red bands match how the CHRO reads a grade at a glance. */
function GradeCell({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  testId: string;
}) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const n = Number(String(value).replace(",", "."));
  const score = Number.isFinite(n) && value !== "" ? n : null;
  const tone = scoreTone(score);

  if (editing) {
    return (
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        className="h-8 text-left font-mono tabular-nums w-14"
        value={value}
        placeholder="—"
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "Escape") {
            setEditing(false);
          }
        }}
        autoFocus
        data-testid={testId}
      />
    );
  }

  return (
    <button
      type="button"
      className="corpus-grade-chip"
      data-tone={tone}
      data-testid={testId}
      onClick={() => setEditing(true)}
      aria-label={
        score !== null ? `Nota ${score}. Clique para editar.` : "Nota vazia. Clique para adicionar."
      }
    >
      <span className="corpus-grade-chip-value">{score !== null ? score.toFixed(1) : "—"}</span>
      <span className="corpus-grade-chip-bar" aria-hidden="true">
        <span
          className="corpus-grade-chip-fill"
          style={{
            width: score !== null ? `${Math.max(0, Math.min(100, (score / 5) * 100))}%` : "0%",
          }}
        />
      </span>
    </button>
  );
}

function scoreTone(score: number | null): string {
  if (score === null) return "empty";
  if (score >= 4.3) return "ex";
  if (score >= 3.5) return "strong";
  if (score >= 2.5) return "mid";
  if (score >= 1.5) return "weak";
  return "low";
}

/**
 * Corpus browser + relabeler. The list view shows each fixture with its
 * hire flag, per-dim expected scores, and "last updated" stamp. Editing
 * a score inline and hitting save writes through the `PATCH` endpoint,
 * which in turn snapshots the old labels into the version log — so every
 * manual relabel is auditable + reversible via the Histórico button.
 *
 * Row-level actions also expose "ver perfil" (opens a sheet with the
 * anonymized LinkedIn dossier) and "remover" (hard-deletes the fixture
 * behind a confirm dialog). The "adicionar perfil" button at the top
 * opens a sheet with URL/JSON input.
 */
export function CorpusSection({ discipline }: { discipline?: string } = {}) {
  const { toast } = useToast();
  const queryKey = ["labs", "calibrationCorpusList", { discipline }];

  const { data: fixtures, isLoading } = useQuery<CorpusFixture[]>({
    queryKey,
    queryFn: () => rpcClient.labs.calibrationCorpusList({ discipline }),
  });

  // One round-trip to know which corpus rows have any correction history —
  // used to badge fixtures with the "✎ ajustada" pill so the CHRO can scan
  // the list before opening any drawer. Exclude rejected/reverted/conflict/
  // failed so a row only lights up when there's a real, live edit.
  // `legacy=1` opts into the bare-array response (this consumer never
  // paginates — it just needs the badge signal across the whole table).
  const { data: corrections } = useQuery<AuditRowSlim[]>({
    queryKey: ["labs", "correctionsAuditList", { legacyStats: true, discipline }],
    queryFn: () =>
      rpcClient.labs.correctionsAuditList({
        limit: 500,
        legacy: true,
        discipline,
      }) as Promise<AuditRowSlim[]>,
  });

  const correctedEvaluationIds = useMemo(() => {
    const set = new Set<string>();
    if (!corrections) return set;
    const excluded = new Set(["rejected", "reverted", "conflict", "failed"]);
    for (const c of corrections) {
      if (!c.evaluationId) continue;
      if (excluded.has(c.status)) continue;
      set.add(c.evaluationId);
    }
    return set;
  }, [corrections]);

  // Derive skill columns from the active discipline (canonical order) or from
  // the union of keys observed across loaded fixtures (legacy fallback).
  const skillKeys = useSkillKeysForDiscipline(discipline, fixtures ?? []);

  // Local editable copy, keyed by view → fixture.id → { key → stringified score }.
  // Split per-view so switching tabs doesn't clobber pending edits on the
  // other dimension set.
  const [evidenceEdits, setEvidenceEdits] = useState<
    Record<string, Partial<Record<DimKey, string>>>
  >({});
  const [skillEdits, setSkillEdits] = useState<Record<string, Partial<Record<string, string>>>>({});
  const [view, setView] = useState<CorpusView>("evidences");
  const [profileFixtureId, setProfileFixtureId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CorpusFixture | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const uploadCorpus = useMutation({
    mutationFn: async (payload: { fixtures: unknown[]; replace?: boolean }) => {
      return rpcClient.labs.calibrationRunsUploadCorpus(payload);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: "Corpus importado",
        description: `${result.upserted ?? 0} perfis upserted${result.replaced ? " (base substituída)" : ""}.`,
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Falha ao importar corpus",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleUploadFile = async (file: File) => {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as { fixtures?: unknown[] };
      if (!parsed || !Array.isArray(parsed.fixtures)) {
        throw new Error("arquivo precisa ter { fixtures: [...] }");
      }
      uploadCorpus.mutate({ fixtures: parsed.fixtures, replace: false });
    } catch (err) {
      toast({
        title: "Arquivo inválido",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  const edits = view === "evidences" ? evidenceEdits : skillEdits;
  const setEdits =
    view === "evidences"
      ? (setEvidenceEdits as (
          updater: (
            prev: Record<string, Partial<Record<string, string>>>,
          ) => Record<string, Partial<Record<string, string>>>,
        ) => void)
      : (setSkillEdits as (
          updater: (
            prev: Record<string, Partial<Record<string, string>>>,
          ) => Record<string, Partial<Record<string, string>>>,
        ) => void);

  const resetRow = (id: string) => {
    setEdits((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const rowHasEdits = (id: string): boolean => {
    const row = edits[id];
    return Boolean(row && Object.keys(row).length > 0);
  };

  const saveRow = useMutation({
    mutationFn: async ({
      id,
      scores,
      target,
    }: {
      id: string;
      scores: Record<string, number>;
      target: CorpusView;
    }) => {
      await rpcClient.labs.calibrationCorpusUpdateLabels({
        id,
        ...(target === "evidences" ? { expectedScores: scores } : { expectedSkills: scores }),
      });
    },
    onSuccess: (_, vars) => {
      resetRow(vars.id);
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Labels atualizadas" });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    },
  });

  const toggleDisabled = useMutation({
    mutationFn: async ({ id, disabled }: { id: string; disabled: boolean }) => {
      await rpcClient.labs.calibrationCorpusToggleDisabled({ id, disabled });
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        title: vars.disabled ? "Perfil removido da calibração" : "Perfil incluído na calibração",
      });
    },
    onError: (err: Error) => {
      toast({ title: "Falha ao alternar", description: err.message, variant: "destructive" });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await rpcClient.labs.calibrationCorpusRemove({ id });
    },
    onSuccess: () => {
      setPendingDelete(null);
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Perfil removido do corpus" });
    },
    onError: (err: Error) => {
      toast({ title: "Falha ao remover", description: err.message, variant: "destructive" });
    },
  });

  const counts = useMemo(() => {
    if (!fixtures) return { total: 0, hired: 0, notHired: 0, pending: 0 };
    let hired = 0;
    let pending = 0;
    for (const f of fixtures) {
      const decision = f.decision ?? (f.hired ? "hired" : "rejected");
      if (decision === "hired") hired++;
      else if (decision === "pending") pending++;
    }
    return {
      total: fixtures.length,
      hired,
      notHired: fixtures.length - hired - pending,
      pending,
    };
  }, [fixtures]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-sm text-muted-foreground max-w-prose">
          <strong>{counts.total}</strong> perfis de calibração (<strong>{counts.hired}</strong>{" "}
          contratados · <strong>{counts.notHired}</strong> não contratados
          {counts.pending > 0 ? (
            <>
              {" · "}
              <strong>{counts.pending}</strong> pendentes
            </>
          ) : null}
          ). Cada correção da CHRO é testada contra estes perfis — editar uma nota aqui ajusta o
          alvo que o agente tenta preservar. Perfis pendentes ficam de fora até você decidir.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <input
            ref={uploadInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUploadFile(file);
              e.target.value = "";
            }}
            data-testid="corpus-upload-input"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => uploadInputRef.current?.click()}
            disabled={uploadCorpus.isPending}
            data-testid="corpus-upload-trigger"
          >
            {uploadCorpus.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5 mr-1.5" />
            )}
            Importar JSON
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="corpus-add-trigger">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Adicionar perfil
          </Button>
        </div>
      </div>

      {!fixtures || fixtures.length === 0 ? (
        <div className="rounded border border-dashed py-10 px-6 text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Corpus vazio. Gere um bundle local com{" "}
            <code>tsx server/scripts/export-corpus-payload.ts --out=corpus.json</code> e importe
            aqui via <strong>Importar JSON</strong>, ou adicione perfis manualmente.
          </p>
        </div>
      ) : (
        <>
          <div className="corpus-view-toggle" role="tablist" aria-label="Tipo de nota">
            <button
              type="button"
              role="tab"
              aria-selected={view === "evidences"}
              onClick={() => setView("evidences")}
              className={`corpus-view-tab${view === "evidences" ? " active" : ""}`}
              data-testid="corpus-view-evidences"
            >
              <span className="corpus-view-label">Notas por evidência</span>
              <span className="corpus-view-hint">
                acadêmico, histórico, crescimento, estabilidade, fundador
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === "skills"}
              onClick={() => setView("skills")}
              className={`corpus-view-tab${view === "skills" ? " active" : ""}`}
              data-testid="corpus-view-skills"
            >
              <span className="corpus-view-label">Nível esperado por skill</span>
              <span className="corpus-view-hint">PM, SM, Business, HR, Product</span>
            </button>
          </div>
          <div className="border rounded-md overflow-x-auto" data-testid="corpus-table">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Perfil</th>
                  <th className="text-left px-3 py-2 font-semibold">Decisão</th>
                  {view === "evidences"
                    ? DIM_KEYS.map((dim) => (
                        <th key={dim} className="text-left px-2 py-2 font-semibold">
                          {DIM_LABEL[dim]}
                        </th>
                      ))
                    : skillKeys.map((sk) => (
                        <th key={sk} className="text-left px-2 py-2 font-semibold">
                          {skillLabelFor(sk, discipline)}
                        </th>
                      ))}
                  <th className="text-right px-3 py-2 font-semibold">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {fixtures.map((f) => {
                  const rowEdits = edits[f.id] ?? {};
                  const dirty = rowHasEdits(f.id);
                  const sourceEvalId = extractSourceEvaluationId(f.candidateName);
                  const hasCorrections =
                    sourceEvalId !== null && correctedEvaluationIds.has(sourceEvalId);
                  return (
                    <tr
                      key={f.id}
                      data-testid={`corpus-row-${f.id}`}
                      className={f.disabled ? "opacity-50" : ""}
                    >
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="font-medium hover:underline underline-offset-2 decoration-dotted"
                          onClick={() => setProfileFixtureId(f.id)}
                          data-testid={`corpus-view-${f.id}`}
                        >
                          {f.displayName}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        {(() => {
                          const decision = f.decision ?? (f.hired ? "hired" : "rejected");
                          if (decision === "pending") {
                            return (
                              <span
                                className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded"
                                style={{
                                  background: "rgba(120,113,108,0.12)",
                                  color: "#57534e",
                                }}
                                title="Decisão pendente — perfil fica fora das calibrações até você decidir contratado/não contratado"
                              >
                                Pendente
                              </span>
                            );
                          }
                          const hired = decision === "hired";
                          return (
                            <span
                              className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded"
                              style={{
                                background: hired ? "rgba(15,44,89,0.08)" : "rgba(244,54,76,0.08)",
                                color: hired ? "#001e3c" : "#f4364c",
                              }}
                            >
                              {hired ? "Sim" : "Não"}
                            </span>
                          );
                        })()}
                        {hasCorrections && (
                          <span
                            className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ml-2"
                            style={{ background: "rgb(254 243 199)", color: "rgb(146 64 14)" }}
                            data-testid={`corpus-corrected-${f.id}`}
                            title="Existem correções pendentes ou aplicadas para este perfil"
                          >
                            ✎ ajustada
                          </span>
                        )}
                      </td>
                      {(view === "evidences" ? (DIM_KEYS as readonly string[]) : skillKeys).map(
                        (key) => {
                          const source =
                            view === "evidences"
                              ? f.expectedScores
                              : normalizeSkillScores(f.expectedSkills ?? {}, discipline);
                          const rowMap = rowEdits as Partial<Record<string, string>>;
                          const raw =
                            rowMap[key] !== undefined
                              ? rowMap[key]
                              : source[key] !== undefined
                                ? String(source[key])
                                : "";
                          return (
                            <td key={key} className="px-2 py-1">
                              <GradeCell
                                value={raw ?? ""}
                                onChange={(next) =>
                                  setEdits((prev) => ({
                                    ...prev,
                                    [f.id]: { ...(prev[f.id] ?? {}), [key]: next },
                                  }))
                                }
                                testId={`corpus-input-${f.id}-${key}`}
                              />
                            </td>
                          );
                        },
                      )}
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className={`h-8 w-8 p-0 ${f.disabled ? "text-muted-foreground" : ""}`}
                            onClick={() =>
                              toggleDisabled.mutate({ id: f.id, disabled: !f.disabled })
                            }
                            aria-label={
                              f.disabled
                                ? `Incluir ${f.displayName} na calibração`
                                : `Pausar ${f.displayName} na calibração`
                            }
                            title={
                              f.disabled
                                ? "Fora da calibração — clique para incluir de volta"
                                : "Na calibração — clique para pausar"
                            }
                            disabled={toggleDisabled.isPending}
                            data-testid={`corpus-toggle-${f.id}`}
                          >
                            {f.disabled ? (
                              <Play className="h-3.5 w-3.5" />
                            ) : (
                              <Pause className="h-3.5 w-3.5 text-foreground" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => setProfileFixtureId(f.id)}
                            aria-label={`Ver perfil de ${f.displayName}`}
                            data-testid={`corpus-peek-${f.id}`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <VersionsPanel
                            kind="calibration_fixture"
                            fixtureId={f.id}
                            label={`Histórico · ${f.displayName}`}
                            onRollback={() => queryClient.invalidateQueries({ queryKey })}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 hover:text-rose-600"
                            onClick={() => setPendingDelete(f)}
                            aria-label={`Remover ${f.displayName}`}
                            data-testid={`corpus-remove-${f.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            // The default Button variant is brand red — too close
                            // to the destructive Trash sitting next to it. The
                            // ghost variant + a navy hover ring keeps Save
                            // visually distinct from danger while staying
                            // recognizably the row's primary action when dirty.
                            variant="ghost"
                            className={`corpus-row-save${dirty ? " is-dirty" : ""}`}
                            onClick={() => {
                              const base =
                                view === "evidences"
                                  ? f.expectedScores
                                  : normalizeSkillScores(f.expectedSkills ?? {}, discipline);
                              const merged: Record<string, number> = { ...base };
                              for (const [key, raw] of Object.entries(rowEdits)) {
                                if (raw === undefined || raw === "") {
                                  delete merged[key];
                                  continue;
                                }
                                const n = Number(String(raw).replace(",", "."));
                                if (Number.isFinite(n)) merged[key] = n;
                              }
                              saveRow.mutate({ id: f.id, scores: merged, target: view });
                            }}
                            disabled={!dirty || saveRow.isPending}
                            data-testid={`corpus-save-${f.id}`}
                            aria-label={`Salvar notas de ${f.displayName}`}
                            title={dirty ? "Salvar edições da linha" : "Sem edições para salvar"}
                          >
                            {saveRow.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                            ) : (
                              <Save className="h-3 w-3" aria-hidden="true" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p className="text-xs text-muted-foreground">
        Nomes são reais — visíveis para administradores e usuários do Labs. Clique no nome ou no
        olho para abrir o dossiê (carreira, formação, headline) que o modelo realmente lê.
      </p>

      <CorpusProfileDrawer
        fixtureId={profileFixtureId}
        open={profileFixtureId !== null}
        onOpenChange={(v) => {
          if (!v) setProfileFixtureId(null);
        }}
      />

      <CorpusAddDrawer
        open={addOpen}
        onOpenChange={setAddOpen}
        onAdded={() => queryClient.invalidateQueries({ queryKey })}
      />

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(v) => {
          if (!v && !remove.isPending) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover {pendingDelete?.displayName}?</AlertDialogTitle>
            <AlertDialogDescription>
              O perfil sai do corpus e não participará mais das próximas calibrações. O histórico de
              versões da rotulagem é preservado — nada é re-escrevível, mas o perfil em si não
              volta. Para recolocar, use "Adicionar perfil".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingDelete && remove.mutate(pendingDelete.id)}
              disabled={remove.isPending}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="corpus-remove-confirm"
            >
              {remove.isPending ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-2" />
                  Removendo…
                </>
              ) : (
                "Remover"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
